import { useCallback } from "react";
import type { Location, NavigateOptions } from "react-router";
import { useLocation, useNavigate } from "react-router";

export type ReturnToState = Readonly<{
    pathname: string;
    search?: string;
    label?: string;
}>;

export type NavigationStateWithReturnTo = Readonly<{
    returnTo?: ReturnToState;
}>;

const buildPath = ({ pathname, search }: ReturnToState): string => `${pathname}${search ?? ""}`;

/**
 * Builds navigate options that carry the current location as return target.
 * label does not change navigation behavior; it’s a placeholder for possible future use (for example: telemetry, debug tracing, breadcrumbs, or “Back to X” button text).
 */
export const withReturnTo = (location: Pick<Location, "pathname" | "search">, label?: string): NavigateOptions => ({
    state: {
        returnTo: {
            pathname: location.pathname,
            search: location.search,
            label,
        },
    } satisfies NavigationStateWithReturnTo,
});

/**
 * Resolves "go back" behavior from navigation state with a canonical fallback.
 */
export const useReturnTo = (fallbackUrl: string) => {
    const navigate = useNavigate();
    const location = useLocation();
    const state = location.state as NavigationStateWithReturnTo | null;
    const returnTo = state?.returnTo;

    return useCallback(() => {
        if (returnTo?.pathname) {
            navigate(buildPath(returnTo));
            return;
        }

        navigate(fallbackUrl);
    }, [fallbackUrl, navigate, returnTo]);
};

/**
 * Resolves "go back" behavior from navigation state with a canonical fallback, as a URL string
 * rather than a navigate call. Useful when the target is needed for something other than an
 * immediate navigation (e.g. as the post-save redirect passed into useFormSubmitCompletion).
 */
export const useReturnToUrl = (fallbackUrl: string): string => {
    const location = useLocation();
    const state = location.state as NavigationStateWithReturnTo | null;
    const returnTo = state?.returnTo;

    return returnTo?.pathname ? buildPath(returnTo) : fallbackUrl;
};

/**
 * Reuses existing returnTo state when moving to sibling routes.
 */
export const useForwardReturnTo = (): NavigateOptions | undefined => {
    const location = useLocation();
    const state = location.state as NavigationStateWithReturnTo | null;

    if (!state?.returnTo) {
        return undefined;
    }

    return {
        state: {
            returnTo: state.returnTo,
        } satisfies NavigationStateWithReturnTo,
    };
};
